
import {useState, useEffect} from 'react';
import {
    LicenseClassGetPaging,
    LicenseClassSave,
    LicenseClassDelete
} from 'services/licenseClassService';
import {LicenseClass, SearchPaging} from 'models/model';
import {LicenseClassModal} from "components/SaveLicenseModal";
import {FaEdit, FaPlus, FaTrash} from "react-icons/fa";
import ConfirmDialog from "popup/ConfirmDialog";
import {toast} from "react-hot-toast";
import LoadingOverlay from "components/Static/LoadingOverlay";
import Pagination from "components/Pagination";
import { Edit, Plus, Trash } from 'lucide-react';

export default function useLicenseClasses() {
    const [licenseClasses, setLicenseClasses] = useState<LicenseClass[]>([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentLicenseClass, setCurrentLicenseClass] = useState<LicenseClass | null>(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [selectedLicenseId, setSelectedLicenseId] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        pageIndex: 1,
        pageSize: 10,
        totalRecords: 0,
        totalPages: 1
    });
    const fetchLicenseClasses = async (pageIndex: number ) => {
        try {
            setLoading(true);
            const response = await LicenseClassGetPaging({
                pageIndex,
                pageSize: pagination.pageSize
            });
            if (response.result) {
                setLicenseClasses(response.result.items);
                setPagination(prev => ({
                    ...prev,
                    pageIndex,
                    totalRecords: response.result.totalRecord,
                    totalPages: Math.ceil(response.result.totalRecord / pagination.pageSize)
                }));
            } else {
                setError(response.message || 'Failed to fetch license classes');
            }
        } catch (err) {
            setError('An error occurred while fetching license classes');
        } finally {
            setLoading(false);
        }
    };

    const saveLicenseClass = async (data: LicenseClass) => {
        try {
            setLoading(true);
            const response = await LicenseClassSave(data);

            if (response.result) {
                toast.success('Lưu hạng đào tạo thành công');
                setIsModalOpen(false);
                await fetchLicenseClasses(pagination.pageIndex);
                return {success: true};
            } else {
                toast.error(response.message || 'Lỗi khi lưu hạng đào tạo');
                return {success: false, message: response.message};
            }
        } catch (err : any) {
            toast.error(err.message || 'Đã có lỗi xảy ra khi lưu hạng đào tạo');
            return {success: false, message: err.message};
        } finally {
            setLoading(false);
        }
    };

    const deleteLicenseClass = async (id: string) => {
        try {
            setLoading(true);
            const response = await LicenseClassDelete(id);

            if (response.result) {
                toast.success('Xóa hạng đào tạo thành công');
                await fetchLicenseClasses(pagination.pageIndex);
                return {success: true};
            } else {
                toast.error(response.message || 'Lỗi khi xóa hạng đào tạo');
                return {success: false, message: response.message};
            }
        } catch (error : any) {
            toast.error(error.message || 'Đã có lỗi xảy ra khi xóa hạng đào tạo');
            return {success: false, message: error.message};
        } finally {
            setLoading(false);
        }
    };
    const handlePageChange = (newPageIndex: number) => {
        if (newPageIndex < 1 || newPageIndex > pagination.totalPages) return;
        fetchLicenseClasses(newPageIndex);
    };

    const handleAddNew = () => {
        setCurrentLicenseClass(null);
        setIsModalOpen(true);
    };

    const handleEdit = (licenseClass: LicenseClass) => {
        setCurrentLicenseClass(licenseClass);
        setIsModalOpen(true);
    };

    const openConfirm = (id: string) => {
        setSelectedLicenseId(id);
        setIsConfirmOpen(true);
    };

    const handleDelete = async () => {
        if (selectedLicenseId === null) return;
        await deleteLicenseClass(selectedLicenseId);
        setIsConfirmOpen(false);
    };

    useEffect(() => {
        fetchLicenseClasses(1);
    }, []);

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h1 className="text-3xl font-bold text-gray-800">Quản lý hạng đào tạo</h1>

            <div className="flex justify-end items-center mb-6 mt-6">
                <button
                    onClick={handleAddNew}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    <Plus/>
                    Thêm hạng đào tạo
                </button>
            </div>

            {isLoading ? <LoadingOverlay message="Đang tải dữ liệu..."/> : (
                <div className="mx-auto">
                    <div className="grid grid-cols-1 gap-8">
                        <div className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="w-full overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">STT</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Tên
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Độ ưu tiên
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Mô tả
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Thao tác
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                    {licenseClasses.map((licenseClass, index) => (
                                        <tr key={licenseClass.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-gray-500">
                                                {(pagination.pageIndex - 1) * pagination.pageSize + index + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-medium text-gray-900">
                                                    {licenseClass.name}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-center text-gray-500">
                                                    {licenseClass.priority}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-gray-500">
                                                    {licenseClass.description || 'Chưa có mô tả'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                <button
                                                    onClick={() => handleEdit(licenseClass)}
                                                    className="text-blue-600 hover:text-blue-900 mr-4"
                                                >
                                                    <Edit className="inline mr-1"/> Sửa
                                                </button>
                                                <button
                                                    onClick={() => licenseClass.id && openConfirm(licenseClass.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    <Trash className="inline mr-1"/> Xóa
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Pagination */}
                        <Pagination
                            pagination={pagination}
                            isLoading={isLoading}
                            onPageChange={handlePageChange}
                            label="hạng đào tạo"
                        />
                    </div>
                </div>
            )}

            <LicenseClassModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={saveLicenseClass}
                initialData={currentLicenseClass}
            />

            <ConfirmDialog
                isOpen={isConfirmOpen}
                title="Xác nhận xóa"
                description="Bạn có chắc chắn muốn xóa hạng đào tạo này không?"
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleDelete}
            />
        </div>
    );
}